import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "@tournated-com/ui";
import { jwtDecode } from "jwt-decode";

const ACCESS_TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const ACCESS_TOKEN_EXPIRES_AT_KEY = "accessTokenExpiresAt";

// `useLocalStorage` keeps its React state in sync by listening for this event
// (its own setter dispatches it). Modules that write a cell directly — the
// refresh queue, the handoff exchange — must dispatch it too, otherwise the
// AuthContext still holds the previous token and AuthGuard would bounce a
// freshly-refreshed session to the portal.
const notifyLocalStorageChange = (key: string) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new StorageEvent("local-storage", { key: `tournated:${key}` }));
};

const readStringItem = (key: string): string | null => {
  const value = getLocalStorageItem(key);
  return typeof value === "string" && value.length > 0 ? value : null;
};

export const isTokenExpired = (token: string | null) => {
  if (!token) return true;
  try {
    const { exp } = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    return !exp || exp < currentTime;
  } catch (error) {
    console.error("Invalid token:", error);
    return true;
  }
};

/**
 * True when the token names an audience (`aud`) that does not include `app`.
 * The backend rejects such a token on every app-scoped controller with a 403
 * (`AppAudienceGuard`), which no refresh can fix — a refresh keeps the family's
 * audience. Legacy tokens carry no `aud` and are never foreign.
 */
export const hasForeignAudience = (token: string | null, app: string): boolean => {
  if (!token) return false;
  try {
    const { aud } = jwtDecode(token);
    if (aud === undefined) return false;
    return Array.isArray(aud) ? !aud.includes(app) : aud !== app;
  } catch {
    return false;
  }
};

export const getAccessToken = (): string | null => readStringItem(ACCESS_TOKEN_KEY);

export const setAccessToken = (accessToken: string) => {
  setLocalStorageItem(ACCESS_TOKEN_KEY, accessToken);
  notifyLocalStorageChange(ACCESS_TOKEN_KEY);
};

export const clearAccessToken = () => {
  removeLocalStorageItem(ACCESS_TOKEN_KEY);
  notifyLocalStorageChange(ACCESS_TOKEN_KEY);
};

/**
 * Opaque, rotating refresh token (`rt_…`). Absent for legacy sessions — every
 * refresh code path is a no-op without it, so those behave exactly as before.
 */
export const getRefreshToken = (): string | null => readStringItem(REFRESH_TOKEN_KEY);

export const setRefreshToken = (refreshToken: string) => {
  setLocalStorageItem(REFRESH_TOKEN_KEY, refreshToken);
};

export const clearRefreshToken = () => {
  removeLocalStorageItem(REFRESH_TOKEN_KEY);
};

/**
 * ISO expiry of the stored access token, as reported by the auth API. Only a
 * hint — the JWT's own `exp` is the fallback when this cell is absent.
 */
export const getAccessTokenExpiresAt = (): string | null =>
  readStringItem(ACCESS_TOKEN_EXPIRES_AT_KEY);

export const setAccessTokenExpiresAt = (expiresAt: string) => {
  setLocalStorageItem(ACCESS_TOKEN_EXPIRES_AT_KEY, expiresAt);
};

export const clearAccessTokenExpiresAt = () => {
  removeLocalStorageItem(ACCESS_TOKEN_EXPIRES_AT_KEY);
};

/** Drops every auth cell at once (refresh rejected, logout, cross-tab logout). */
export const clearAuthTokens = () => {
  clearAccessToken();
  clearRefreshToken();
  clearAccessTokenExpiresAt();
};
